//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

/**
 * Switch Component
 *
 * A reusable toggle switch component for boolean state selection.
 * Wraps Radix UI's Switch primitive and provides consistent styling.
 * Supports disabled state and focus indicators.
 *
 * Usage:
 * <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
 */

import React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cn } from './utils';

type SwitchSize = 'default' | 'sm';

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
    size?: SwitchSize;
  }
>(({ className, size = 'default', ...props }, ref) => (
  <SwitchPrimitives.Root
    ref={ref}
    className={cn(
      'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-blue-500 data-[state=unchecked]:bg-gray-300',
      size === 'sm' ? 'h-4 w-7' : 'h-6 w-11',
      className,
    )}
    {...props}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        'pointer-events-none block rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=unchecked]:translate-x-0',
        size === 'sm'
          ? 'size-3 data-[state=checked]:translate-x-3'
          : 'size-5 data-[state=checked]:translate-x-5',
      )}
    />
  </SwitchPrimitives.Root>
));
Switch.displayName = 'Switch';

export { Switch };
